import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { atestadoService } from '../../services/api';

const Atestados = () => {
  const { user } = useAuth();
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10,
    status: '',
    tipo: '',
    colaborador: ''
  });
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [atestadoSelecionado, setAtestadoSelecionado] = useState(null);
  const [modalHomologacao, setModalHomologacao] = useState(false);
  const [atestadoParaHomologar, setAtestadoParaHomologar] = useState(null);
  const [modalRecusa, setModalRecusa] = useState(false);
  const [atestadoParaRecusar, setAtestadoParaRecusar] = useState(null);
  
  const [formData, setFormData] = useState({
    colaborador: '',
    tipo: 'medico',
    numeroAtestado: '',
    dataEmissao: '',
    dataInicio: '',
    dataFim: '',
    dias: '',
    cid: '',
    observacoes: '',
    foto: null
  });

  const [homologacaoData, setHomologacaoData] = useState({
    diasJustificativa: '',
    observacoesHomologacao: ''
  });

  const [recusaData, setRecusaData] = useState({
    motivoRecusa: '',
    observacoesRecusa: ''
  });

  const queryClient = useQueryClient();

  // Verificar se o usuário tem permissão para homologar atestados
  const podeHomologar = ['administrador', 'supervisor'].includes(user?.perfil);

  // Buscar atestados da API
  const { data: atestadosData, isLoading, error } = useQuery({
    queryKey: ['atestados', filtros],
    queryFn: () => {
      console.log('🔍 Chamando atestadoService.listar com filtros:', filtros);
      return atestadoService.listar(filtros);
    },
    keepPreviousData: true
  });

  // Garantir que temos um array válido para trabalhar
  const atestados = Array.isArray(atestadosData?.data?.data?.atestados) ? atestadosData.data.data.atestados : [];
  
  // Debug: verificar dados recebidos
  console.log('🔍 Debug Atestados RH:');
  console.log('   isLoading:', isLoading);
  console.log('   error:', error);
  console.log('   atestadosData:', atestadosData);
  console.log('   atestadosData.data:', atestadosData?.data);
  console.log('   atestadosData.data.data:', atestadosData?.data?.data);
  console.log('   atestadosData.data.data.atestados:', atestadosData?.data?.data?.atestados);
  console.log('   atestados array:', atestados);
  console.log('   atestados length:', atestados.length);

  // Mutação para criar atestado
  const createMutation = useMutation({
    mutationFn: atestadoService.criar,
    onSuccess: () => {
      toast.success('Atestado criado com sucesso!');
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar atestado');
    }
  });

  // Mutação para atualizar atestado
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => atestadoService.atualizar(id, data),
    onSuccess: () => {
      toast.success('Atestado atualizado com sucesso!');
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar atestado');
    }
  });

  // Mutação para aprovar atestado
  const approveMutation = useMutation({
    mutationFn: ({ id, data }) => atestadoService.aprovar(id, data),
    onSuccess: () => {
      toast.success('Atestado aprovado com sucesso!');
      setModalHomologacao(false);
      setHomologacaoData({ diasJustificativa: '', observacoesHomologacao: '' });
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar atestado');
    }
  });

  // Mutação para recusar atestado
  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => atestadoService.recusar(id, data),
    onSuccess: () => {
      toast.success('Atestado recusado com sucesso!');
      setModalRecusa(false);
      setRecusaData({ motivoRecusa: '', observacoesRecusa: '' });
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao recusar atestado');
    }
  });

  // Mutação para reabrir atestado
  const reopenMutation = useMutation({
    mutationFn: atestadoService.reabrir,
    onSuccess: () => {
      toast.success('Atestado reaberto com sucesso!');
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao reabrir atestado');
    }
  });

  // Mutação para deletar atestado
  const deleteMutation = useMutation({
    mutationFn: atestadoService.deletar,
    onSuccess: () => {
      toast.success('Atestado removido com sucesso!');
      queryClient.invalidateQueries(['atestados']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover atestado');
    }
  });

  const handleCreate = () => {
    setModalMode('create');
    setAtestadoSelecionado(null);
    resetForm();
    setModalOpen(true);
  };

  const handleEdit = (atestado) => {
    setModalMode('edit');
    setAtestadoSelecionado(atestado);
    setFormData({
      colaborador: atestado.colaborador?.nome || atestado.colaborador || '',
      tipo: atestado.tipo,
      numeroAtestado: atestado.numeroAtestado,
      dataEmissao: atestado.dataEmissao ? new Date(atestado.dataEmissao).toISOString().split('T')[0] : '',
      dataInicio: atestado.dataInicio ? new Date(atestado.dataInicio).toISOString().split('T')[0] : '',
      dataFim: atestado.dataFim ? new Date(atestado.dataFim).toISOString().split('T')[0] : '',
      dias: atestado.dias,
      cid: atestado.cid,
      observacoes: atestado.observacoes,
      foto: atestado.foto
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalMode === 'create') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({
        id: atestadoSelecionado._id,
        data: formData
      });
    }
  };

  const resetForm = () => {
    setFormData({
      colaborador: '',
      tipo: 'medico',
      numeroAtestado: '',
      dataEmissao: '',
      dataInicio: '',
      dataFim: '',
      dias: '',
      cid: '',
      observacoes: '',
      foto: null
    });
    setAtestadoSelecionado(null);
  };

  const handleHomologar = (atestado) => {
    setAtestadoParaHomologar(atestado);
    setHomologacaoData({
      diasJustificativa: atestado.dias || '',
      observacoesHomologacao: ''
    });
    setModalHomologacao(true);
  };

  const handleRecusar = (atestado) => {
    setAtestadoParaRecusar(atestado);
    setRecusaData({
      motivoRecusa: '',
      observacoesRecusa: ''
    });
    setModalRecusa(true);
  };

  const handleConfirmarHomologacao = () => {
    if (!homologacaoData.diasJustificativa) {
      toast.error('É necessário informar os dias de justificativa de ponto');
      return;
    }
    
    approveMutation.mutate({
      id: atestadoParaHomologar._id,
      data: homologacaoData
    });
  };

  const handleConfirmarRecusa = () => {
    if (!recusaData.motivoRecusa) {
      toast.error('É necessário informar o motivo da recusa');
      return;
    }
    
    rejectMutation.mutate({
      id: atestadoParaRecusar._id,
      data: recusaData
    });
  };

  const handleVisualizarFoto = (foto) => {
    if (foto && foto.url) {
      window.open(`http://localhost:5000${foto.url}`, '_blank');
    } else {
      toast.error('Foto não disponível');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'bg-warning-100 text-warning-800';
      case 'aprovado': return 'bg-success-100 text-success-800';
      case 'recusado': return 'bg-danger-100 text-danger-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pendente': 'Pendente',
      'aprovado': 'Aprovado',
      'recusado': 'Recusado'
    };
    return statusMap[status] || status;
  };

  const getTipoText = (tipo) => {
    const tipos = {
      'medico': 'Médico',
      'odontologico': 'Odontológico',
      'psicologico': 'Psicológico',
      'fisioterapeutico': 'Fisioterapêutico'
    };
    return tipos[tipo] || tipo;
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800">Erro ao carregar atestados</h3>
        <p className="text-red-600">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Atestados</h1>
            <p className="text-gray-600">Gerencie os atestados dos colaboradores</p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <span className="mr-2">➕</span>
            Novo Atestado
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filtros.status}
              onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="aprovado">Aprovado</option>
              <option value="recusado">Recusado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filtros.tipo}
              onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="medico">Médico</option>
              <option value="odontologico">Odontológico</option>
              <option value="psicologico">Psicológico</option>
              <option value="fisioterapeutico">Fisioterapêutico</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Colaborador</label>
            <input
              type="text"
              value={filtros.colaborador}
              onChange={(e) => setFiltros(prev => ({ ...prev, colaborador: e.target.value }))}
              placeholder="Nome do colaborador"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFiltros({ page: 1, limit: 10, status: '', tipo: '', colaborador: '' })}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Atestados */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nº Atestado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dias
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⏳</span>
                      Carregando atestados...
                    </div>
                  </td>
                </tr>
              ) : atestados.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhum atestado encontrado
                  </td>
                </tr>
              ) : (
                atestados.map((atestado) => (
                  <tr key={atestado._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {atestado.colaborador?.nome || atestado.colaborador}
                      </div>
                      <div className="text-sm text-gray-500">
                        Matrícula: {atestado.matricula}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">
                        {getTipoText(atestado.tipo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {atestado.numeroAtestado}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {atestado.dataInicio ? new Date(atestado.dataInicio).toLocaleDateString('pt-BR') : '-'} a {atestado.dataFim ? new Date(atestado.dataFim).toLocaleDateString('pt-BR') : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {atestado.dias} dias
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(atestado.status)}`}>
                        {getStatusText(atestado.status)}
                      </span>
                      {atestado.status === 'aprovado' && atestado.diasJustificativa && (
                        <div className="text-xs text-gray-500 mt-1">
                          {atestado.diasJustificativa} dias de ponto justificados
                        </div>
                      )}
                      {atestado.status === 'recusado' && atestado.motivoRecusa && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={atestado.motivoRecusa}>
                          {atestado.motivoRecusa}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(atestado)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        
                        {atestado.foto && atestado.foto.url && (
                          <button
                            onClick={() => handleVisualizarFoto(atestado.foto)}
                            className="text-green-600 hover:text-green-900"
                            title="Visualizar Foto"
                          >
                            📷
                          </button>
                        )}
                        
                        {atestado.status === 'pendente' && podeHomologar && (
                          <>
                            <button
                              onClick={() => handleHomologar(atestado)}
                              className="text-green-600 hover:text-green-900"
                              title="Aprovar"
                            >
                              ✅
                            </button>
                            <button
                              onClick={() => handleRecusar(atestado)}
                              className="text-red-600 hover:text-red-900"
                              title="Recusar"
                            >
                              ❌
                            </button>
                          </>
                        )}
                        
                        {atestado.status === 'pendente' && !podeHomologar && (
                          <span className="text-gray-400 text-xs">
                            Aguardando homologação
                          </span>
                        )}
                        
                        {atestado.status !== 'pendente' && podeHomologar && (
                          <button
                            onClick={() => reopenMutation.mutate(atestado._id)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Reabrir"
                          >
                            🔄
                          </button>
                        )}
                        
                        {podeHomologar && (
                          <button
                            onClick={() => {
                              if (window.confirm('Tem certeza que deseja remover este atestado?')) {
                                deleteMutation.mutate(atestado._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Remover"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {modalMode === 'create' ? 'Novo Atestado' : 'Editar Atestado'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Colaborador</label>
                    <input
                      type="text"
                      value={formData.colaborador}
                      onChange={(e) => setFormData(prev => ({ ...prev, colaborador: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="medico">Médico</option>
                      <option value="odontologico">Odontológico</option>
                      <option value="psicologico">Psicológico</option>
                      <option value="fisioterapeutico">Fisioterapêutico</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número do Atestado</label>
                    <input
                      type="text"
                      value={formData.numeroAtestado}
                      onChange={(e) => setFormData(prev => ({ ...prev, numeroAtestado: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Emissão</label>
                    <input
                      type="date"
                      value={formData.dataEmissao}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataEmissao: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início</label>
                    <input
                      type="date"
                      value={formData.dataInicio}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataInicio: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Fim</label>
                    <input
                      type="date"
                      value={formData.dataFim}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataFim: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dias</label>
                    <input
                      type="number"
                      value={formData.dias}
                      onChange={(e) => setFormData(prev => ({ ...prev, dias: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CID</label>
                    <input
                      type="text"
                      value={formData.cid}
                      onChange={(e) => setFormData(prev => ({ ...prev, cid: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {modalMode === 'create' ? 'Criar' : 'Atualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Homologação */}
      {modalHomologacao && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Homologar Atestado
              </h3>
              <form onSubmit={(e) => { e.preventDefault(); handleConfirmarHomologacao(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dias de Justificativa de Ponto
                  </label>
                  <input
                    type="number"
                    value={homologacaoData.diasJustificativa}
                    onChange={(e) => setHomologacaoData(prev => ({ ...prev, diasJustificativa: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações da Homologação
                  </label>
                  <textarea
                    value={homologacaoData.observacoesHomologacao}
                    onChange={(e) => setHomologacaoData(prev => ({ ...prev, observacoesHomologacao: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModalHomologacao(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Confirmar Homologação
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Recusa */}
      {modalRecusa && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Recusar Atestado
              </h3>
              <form onSubmit={(e) => { e.preventDefault(); handleConfirmarRecusa(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo da Recusa
                  </label>
                  <select
                    value={recusaData.motivoRecusa}
                    onChange={(e) => setRecusaData(prev => ({ ...prev, motivoRecusa: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecione um motivo</option>
                    <option value="falta_de_justificativa">Falta de Justificativa</option>
                    <option value="informacao_incompleta">Informação Incompleta</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações da Recusa
                  </label>
                  <textarea
                    value={recusaData.observacoesRecusa}
                    onChange={(e) => setRecusaData(prev => ({ ...prev, observacoesRecusa: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModalRecusa(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Confirmar Recusa
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Atestados;